import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ThemeService } from '../../service/theme.service';
import { IotService } from '../../service/iot.service';
import { BinsService } from '../../service/bins.service';
import { ClientService } from '../../service/client.service';
import { DistributorsService } from '../../service/distributors.service';
import { ModalService } from '../../service/device-modal.service';
import { IotDevicesViewService } from '../iot-devices-view.service';
import { S3Service } from '../../service/s3.service';
import { SystemMessageService } from '../../service/system-message.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-iot-devices-table',
  templateUrl: './iot-devices-table.component.html',
  styleUrls: ['./iot-devices-table.component.css'],
  standalone: false,
})
export class IotDevicesTableComponent implements OnInit {
  hideMap = true;
  lastExpanded = '';
  address = '';
  markers: any[] = [];
  markersMobile: any[] = [];
  map: any;
  mapMobile: any;
  mapCoords: { latitude: number; longitude: number } = {
    latitude: 45.65054871734081,
    longitude: -73.84789617258333,
  };
  currentDevice: any;
  @ViewChild('mapContainer') mapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('mapContainerMobile')
  mapContainerMobile?: ElementRef<HTMLDivElement>;

  constructor(
    public theme: ThemeService,
    public iotService: IotService,
    public binService: BinsService,
    public view: IotDevicesViewService,
    public modal: ModalService,
    private s3: S3Service,
    private systemMessage: SystemMessageService,
    private clientService: ClientService,
    private distributorService: DistributorsService,
    private router: Router,
    public translate: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.view.init();
  }

  get thingGroupOptions() {
    return [
      { label: 'allGroups', value: '' },
      { label: 'DEV', value: 'dev' },
      { label: 'POC', value: 'poc' },
      { label: 'PRD', value: 'prd' },
    ];
  }

  filteredDevices() {
    return this.view.filteredDevices();
  }

  calculateFillPercentage(height?: number, dst?: number) {
    if (!height || height <= 0 || dst === undefined || dst === null) {
      return 0;
    }
    const pct = Math.round(((height - dst)/height) * 100);
    return Math.max(0, Math.min(100, pct));
  }

  verifyStatus(thingName: string): string {
    return this.iotService.checkHealthStatusUsingThingName
      ? this.iotService.checkHealthStatusUsingThingName(thingName)
      : 'Inactive';
  }

  async showMap(deviceRow: any) {
    this.hideMap = false;
    let gps;
    if (deviceRow.bin?.bin_id) {
      if (deviceRow.bin.bin_gps !== null && deviceRow.bin.bin_gps.length > 0) {
        gps = {
          latitude: Number(deviceRow.bin.bin_gps.split(',')[0]),
          longitude: Number(deviceRow.bin.bin_gps.split(',')[1]),
        };
      } else {
        gps = {
          latitude: Number(deviceRow.bin.bin_location.split(',')[0]),
          longitude: Number(deviceRow.bin.bin_location.split(',')[1]),
        };
      }
      this.lastExpanded = deviceRow.thingName;
      this.address = deviceRow.bin.bin_address;
    } else {
      this.lastExpanded = deviceRow.thingName;
      this.address = 'withoutBin';
    }

    this.mapCoords = gps || this.mapCoords;
    await this.createMap(gps);
    await this.createMapMobile(gps);

    this.fillMarkersArray([deviceRow]);
    this.displayMarkersOnMap();

    this.currentDevice = deviceRow;
  }

  // Function used to refresh device data initialized for production
  async initializeDeviceForProduction(thingName: string) {
    const confirm = window.confirm(
      await firstValueFrom(this.translate.get('initializeDevice'))
    );

    // Wait to user to select confirm on the window message
    if (confirm) {
      try {
        const initialized: any = await firstValueFrom(
          this.iotService.initializeDeviceForProduction(thingName)
        );

        const shadowUpdate = await this.iotService.updateThingShadow(thingName);
        if (initialized.message === 'success') {
          this.systemMessage.selectRibbon('success', 'initializeWithSuccess');
        }
      } catch (error) {
        console.error('Error', error);
        this.systemMessage.selectRibbon(
          'danger',
          'alert-danger-generic-message'
        );
      }
    }
  }

  async showDeviceNotes(thingName: string) {
    await this.iotService.getDeviceNotes(thingName);
    this.modal.deviceNotes = this.iotService.deviceNotes;
    this.modal.showLoadingStatus = false;
    this.modal.showDeviceModal = true;
    this.modal.showNotesModal = true;
  }

  async showRelation(thingName: string) {
    await firstValueFrom(
      await this.iotService.getDeviceRelationship(thingName)
    );
    this.modal.deviceAssociationRow = this.iotService.deviceAssociationRow;

    let array: any = await this.distributorService.getDistributorForSelect();
    this.modal.distributorsArray = array;

    array = await this.clientService.getClientsForSelect();
    this.modal.clientsArray = array;

    const res: any = await firstValueFrom(this.binService.getBinsJson());
    if (res) {
      this.modal.binArray = res;
      this.modal.binArray = this.modal.binArray.filter(
        (bin: any) => bin.thing_name === ''
      );
    }

    this.modal.thingName = thingName;
    this.modal.showDeviceModal = true;
    this.modal.showRelationship = true;
  }

  setNavToDeviceStat(thingName: string) {
    sessionStorage.setItem('toDeviceStats', 'iot/technical');
    this.router.navigate([`/device-statistics/${thingName}`]);
  }

  closeMap() {
    this.hideMap = true;
    this.clearMarkers();
  }

  private clearMarkers() {
    this.markers.forEach((m) => (m.map = null));
    this.markersMobile.forEach((m) => (m.map = null));
    this.markers = [];
    this.markersMobile = [];
  }

  async createMap(
    gps: { latitude: number; longitude: number } = {
      latitude: 0,
      longitude: 0,
    }
  ): Promise<void> {
    let position: any = '';
    if (
      gps.latitude !== 0 ||
      (gps.latitude !== undefined && gps.longitude !== 0)
    ) {
      position = { lat: gps.latitude, lng: gps.longitude };
    } else {
      position = { lat: 45.65054871734081, lng: -73.84789617258333 };
    }
    const { Map } = (await google.maps.importLibrary(
      'maps'
    )) as google.maps.MapsLibrary;
    await google.maps.importLibrary('marker');

    const mapDiv =
      this.mapContainer?.nativeElement || document.getElementById('mapDesktop');
    if (!mapDiv) {
      return;
    }
    const map = new Map(mapDiv as HTMLElement, {
      zoom: 10,
      center: position,
      mapId: 'DEVICES_MAP',
      disableDefaultUI: true,
    });
    this.map = map;
  }

  async createMapMobile(
    gps: { latitude: number; longitude: number } = {
      latitude: 0,
      longitude: 0,
    }
  ): Promise<void> {
    let position: any = '';
    if (
      gps.latitude !== 0 ||
      (gps.latitude !== undefined && gps.longitude !== 0)
    ) {
      position = { lat: gps.latitude, lng: gps.longitude };
    } else {
      position = { lat: 45.65054871734081, lng: -73.84789617258333 };
    }
    const { Map } = (await google.maps.importLibrary(
      'maps'
    )) as google.maps.MapsLibrary;
    await google.maps.importLibrary('marker');

    const host =
      this.mapContainerMobile?.nativeElement ||
      document.getElementById('mapMobile');
    if (!host) {
      return;
    }
    const map = new Map(host as HTMLElement, {
      zoom: 10,
      center: position,
      mapId: 'DEVICES_MAP_MOBILE',
      disableDefaultUI: true,
    });
    this.mapMobile = map;
  }

  fillMarkersArray(devices: any[] | any) {
    this.markers = [];
    this.markersMobile = [];
    if (!devices || devices.length === 0) {
      console.error('No device(s) to fill array');
      return;
    }

    devices.forEach((device: any) => {
      const gps = device.bin?.bin_gps || device.bin?.bin_location;
      if (!gps) return;
      const [lat, lng] = gps.split(',').map(Number);
      const markersOptions = {
        position: { lat, lng },
        title: device.thingName + ', \n' + (device.bin?.bin_address || ''),
      };
      const g: any = (window as any).google;
      if (g?.maps?.marker?.AdvancedMarkerElement) {
        const newMarker = new google.maps.marker.AdvancedMarkerElement(
          markersOptions
        );
        const newMobileMarker = new google.maps.marker.AdvancedMarkerElement(
          markersOptions
        );
        this.markers.push(newMarker);
        this.markersMobile.push(newMobileMarker);
      } else if (g?.maps?.Marker) {
        const newMarker = new google.maps.Marker({
          ...markersOptions,
          map: null,
        });
        const newMobileMarker = new google.maps.Marker({
          ...markersOptions,
          map: null,
        });
        this.markers.push(newMarker);
        this.markersMobile.push(newMobileMarker);
      }
    });
  }

  displayMarkersOnMap() {
    if (this.markers.length <= 0) return;

    if (this.map) {
      this.markers.forEach((marker) => {
        if (marker.setMap) {
          marker.setMap(this.map);
        } else {
          marker.map = this.map;
        }
      });
    } else {
      console.error('Map not initialized');
    }

    if (this.mapMobile) {
      if (this.markersMobile.length > 0) {
        this.markersMobile.forEach((marker) => {
          if (marker.setMap) {
            marker.setMap(this.mapMobile);
          } else {
            marker.map = this.mapMobile;
          }
        });
      } else {
        console.error('Mobile map not initialized');
      }
    }
    this.recenterMap(this.mapCoords);
  }

  private recenterMap(coords: { latitude: number; longitude: number }) {
    if (this.map) {
      this.map.setCenter({ lat: coords.latitude, lng: coords.longitude });
    }
    if (this.mapMobile) {
      this.mapMobile.setCenter({ lat: coords.latitude, lng: coords.longitude });
    }
  }

  async getCertificateFolderUrl(thingName: string) {
    await this.s3.getCertificatesForThing(thingName).then((res) => {
      if (res?.length) {
        const cert = res[0];
        const a = document.createElement('a');
        a.href = cert;
        a.download = `${thingName}-certificates.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        this.systemMessage.selectRibbon(
          'danger',
          'noCertificatesSystemMessage'
        );
      }
    });
  }
}
